import buildInfo from '@/app/version.json';
import type { NuxtApp } from '#app';

export type DatalayerState = {
  userAffinityRedirected: null | String;
  page: null | Record<string, any>;
  products: null | Record<string, any>[];
  cart: null | Record<string, any>;
  user: null | Record<string, any>;
  filter: null | Record<string, any>;
  search: null;
  wishlist: null;
  order: null;
  error: string | null;
  releaseInformation: null | typeof buildInfo;
  bvInformation: null | {
    nickname: string;
    bvEncryptionKeyId: string;
    bvEncryptedEmail: string;
  };
  storeFinder: Record<string, any> | null;
  initParams: {
    initialLoad: boolean;
  };
  conversionRateToEuro: null | number;
};

export const useDatalayerStore = defineStore('datalayer', {
  state: (): DatalayerState => ({
    userAffinityRedirected: null,
    page: null,
    products: null,
    cart: null,
    user: null,
    filter: null,
    search: null,
    wishlist: null,
    order: null,
    error: null,
    releaseInformation: buildInfo,
    bvInformation: null,
    storeFinder: null,
    initParams: {
      initialLoad: true,
    },
    conversionRateToEuro: null,
  }),
  actions: {
    addPageProperties: function (properties: Record<string, any>) {
      this.page = {
        ...this.page,
        ...properties,
      };
    },
    addUserProperties: function (properties: Record<string, any>) {
      this.user = {
        ...this.user,
        ...properties,
      };
    },
    addCartProperties: function (properties: Record<string, any>) {
      this.cart = {
        ...this.cart,
        ...properties,
      };
    },
    addFilterProperties: function (properties: Record<string, any>) {
      this.filter = {
        ...this.filter,
        ...properties,
      };
    },

    loadOrderData: async function (code: string) {
      const { $api } = useNuxtApp();
      logger.debug('Loading order data for datalayer...');

      const userStore = useUserStore();
      const userType = userStore.getType;

      return $api.hybris.analytics
        .get(userType, null, code, [resourceNames.ORDER, resourceNames.USER])
        .then((response: Record<string, any>) => {
          this.addUserProperties(response.data.user);
          this.order = response.data.order;
          this.cart = null;
        })
        .catch((error: any) => {
          logger.error('Failed loading order data for datalayer.', error);
        });
    },
    loadBVInformation: async function () {
      const { $api } = useNuxtApp();
      logger.debug('Loading BV-relevant info for datalayer...');

      const userStore = useUserStore();
      const userType = userStore.getType;

      return $api.hybris.user
        .getBVKeys(userType)
        .then((response: Record<string, any>) => {
          const user = (userStore.user ?? {}) as Record<string, any>;

          this.bvInformation = {
            nickname: `${user.defaultAddress.firstName} ${user.defaultAddress.lastName}`,
            bvEncryptionKeyId: response.data.bvEncryptionKeyId,
            bvEncryptedEmail: response.data.bvEncryptedEmail,
          };
        })
        .catch((error: any) => {
          logger.error('Failed loading BV-relevant data for datalayer.', error);
        });
    },
    loadAnalytics: async function (nuxtApp: NuxtApp, resourceList?: string[]) {
      const { $api } = nuxtApp;
      logger.debug('Loading analytics data...');

      const { getType: userType } = useUserStore();
      const { getCartId: cartId } = useCartStore();

      return $api.hybris.analytics
        .get(userType, cartId, null, resourceList)
        .then((response: Record<string, any>) => {
          if (response.data.user) {
            this.addUserProperties(response.data.user);
          }
          if (response.data.cart) {
            this.addCartProperties(response.data.cart);
          }
          if (response.data.wishlist) {
            this.wishlist = response.data.wishlist;
          }
          if (response.data.order) {
            this.order = response.data.order;
          }
          if (response.data.conversionRateToEuro) {
            this.conversionRateToEuro = response.data.conversionRateToEuro;
          }
        })
        .catch((error: any) => {
          logger.warn('Failed loading user data for datalayer.', error);
          throw error;
        });
    },
    setStoreFinderAnalytics: function (storeFinderData: Record<string, any>) {
      logger.debug('Setting storeFinder data for datalayer...');

      this.storeFinder = storeFinderData;
    },
    setUnsubscribedStatus: function (unsubscribeType: keyof typeof unsubscriptionTypes) {
      logger.debug('Setting unsubscription data for datalayer...');

      this.addUserProperties({ [`unsubscribe-${unsubscribeType}`.toLowerCase()]: true });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDatalayerStore, import.meta.hot));
}
